// Page "Apports d'affaire" — page d'information administrable en WYSIWYG.
// Contenu stocké via la table app_settings (clé apports_affaire.contenu).
// Édition réservée aux utilisateurs avec la perm apports_affaire.edit.

import { useEffect, useRef, useState } from 'react';
import { cleanHtml } from '../utils/sanitize';
import { useAuth } from 'react-oidc-context';
import { setAuthToken, getSetting, updateSetting } from '../services/api';
import { usePerm } from '../lib/perms';
import { toast } from 'react-toastify';
import {
  Pencil, Save, X as XIcon, Bold, Italic, Underline, List,
  ListOrdered, Heading2, Quote, Loader2, BadgePercent,
} from 'lucide-react';

const SETTING_KEY = 'apports_affaire.contenu';

const DEFAULT_HTML = `
<h2>Apports d'affaire — comment ça marche</h2>
<p>
  Le programme d'apports d'affaire récompense les antennes qui amènent de
  nouveaux clients à Konitys. Deux formules sont possibles :
</p>
<h3>Forfait « Code promo »</h3>
<p>
  L'antenne reçoit un montant fixe (par défaut <strong>20&nbsp;€</strong>)
  par client converti, et le client bénéficie d'une remise (par défaut
  <strong>30&nbsp;€</strong>) à l'aide d'un code parrain généré
  automatiquement. Le montant et la remise sont configurables dans la
  fiche tarifaire de chaque antenne.
</p>
<h3>Forfait « Classique »</h3>
<p>
  L'antenne perçoit un pourcentage du prix HT de la borne vendue (par
  défaut <strong>20&nbsp;%</strong>) lors de la conversion d'un nouveau
  client. Ce pourcentage est lui aussi configurable dans la fiche
  tarifaire.
</p>
<h3>Conditions communes</h3>
<ul>
  <li>Le client recommandé doit être nouveau (pas déjà dans la base).</li>
  <li>L'apport est validé une fois le contrat ou la commande signée.</li>
  <li>La rétribution est versée avec la facturation du mois suivant.</li>
</ul>
<p><em>Cette page est administrable. Clique sur le crayon pour la modifier.</em></p>
`;

// ─────────────────────────────────────────────────────────────────────
// Mini éditeur WYSIWYG (inspiré du free-text du Dashboard).
// ─────────────────────────────────────────────────────────────────────
const toolbarBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 30, height: 30, borderRadius: 6,
  border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer',
};

export default function ApportsAffaire() {
  const auth = useAuth();
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const canEdit = usePerm('apports_affaire.edit');

  useEffect(() => {
    if (auth?.user?.access_token) setAuthToken(auth.user.access_token);
  }, [auth?.user?.access_token]);

  useEffect(() => {
    (async () => {
      try {
        const value = await getSetting(SETTING_KEY);
        if (value && typeof value === 'string' && value.trim()) setHtml(value);
      } catch (_) { /* pas encore défini — on garde le default */ }
      setLoading(false);
    })();
  }, []);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
  };

  const startEdit = () => {
    setEditing(true);
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = html;
      editorRef.current?.focus();
    }, 30);
  };

  const cancelEdit = () => setEditing(false);

  const save = async () => {
    if (!editorRef.current) return;
    const newHtml = editorRef.current.innerHTML;
    setSaving(true);
    try {
      await updateSetting(SETTING_KEY, newHtml);
      setHtml(newHtml);
      setEditing(false);
      toast.success('Page mise à jour');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '20px 24px', maxWidth: 920, margin: '0 auto' }}>
      <header style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ width: 40, height: 40, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <BadgePercent size={20} color="#b45309" />
        </span>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>Apports d'affaire</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>
            Programme de récompense des antennes qui amènent de nouveaux clients à Konitys.
          </p>
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={startEdit}
            data-perm="antennes:apports_affaire.edit"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid #e2e8f0', background: 'white', color: '#475569',
              fontSize: 12, cursor: 'pointer',
            }}
          >
            <Pencil size={13} /> Modifier
          </button>
        )}
      </header>

      <section style={{
        background: 'white', border: '1px solid #e2e8f0', borderRadius: 12,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Loader2 size={22} className="spin" style={{ color: '#6366f1' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 0.9s linear infinite; }`}</style>
          </div>
        ) : !editing ? (
          <div
            style={{ padding: '24px 28px', fontSize: 14, lineHeight: 1.6, color: '#0f172a' }}
            className="rich-content"
            dangerouslySetInnerHTML={{ __html: cleanHtml(html) }}
          />
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8, padding: 6,
              background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
            }}>
              <button type="button" onClick={() => exec('bold')} title="Gras" style={toolbarBtnStyle}><Bold size={14} /></button>
              <button type="button" onClick={() => exec('italic')} title="Italique" style={toolbarBtnStyle}><Italic size={14} /></button>
              <button type="button" onClick={() => exec('underline')} title="Souligné" style={toolbarBtnStyle}><Underline size={14} /></button>
              <span style={{ width: 1, background: '#e2e8f0', margin: '4px 4px' }} />
              <button type="button" onClick={() => exec('formatBlock', 'h2')} title="Titre 2" style={{ ...toolbarBtnStyle, fontSize: 11, fontWeight: 700 }}>H2</button>
              <button type="button" onClick={() => exec('formatBlock', 'h3')} title="Titre 3" style={{ ...toolbarBtnStyle, fontSize: 11, fontWeight: 700 }}>H3</button>
              <button type="button" onClick={() => exec('formatBlock', 'p')} title="Paragraphe" style={{ ...toolbarBtnStyle, fontSize: 11 }}>P</button>
              <span style={{ width: 1, background: '#e2e8f0', margin: '4px 4px' }} />
              <button type="button" onClick={() => exec('insertUnorderedList')} title="Liste à puces" style={toolbarBtnStyle}><List size={14} /></button>
              <button type="button" onClick={() => exec('insertOrderedList')} title="Liste numérotée" style={toolbarBtnStyle}><ListOrdered size={14} /></button>
              <button type="button" onClick={() => exec('formatBlock', 'blockquote')} title="Citation" style={toolbarBtnStyle}><Quote size={14} /></button>
            </div>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className="rich-content"
              style={{
                minHeight: 260, padding: '14px 16px',
                border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: 14, lineHeight: 1.6, color: '#0f172a',
                outline: 'none', background: 'white',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  border: '1px solid #e2e8f0', background: 'white', color: '#475569',
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                <XIcon size={14} /> Annuler
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-perm="antennes:apports_affaire.edit"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 8,
                  border: 'none', background: '#4f46e5', color: 'white',
                  fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                Enregistrer
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
